/**
 * useExperienceLevel — Zustand store for the user's experience level.
 *
 * Persists per-org in localStorage so switching orgs can have different views
 * (e.g. your own shop on Flow, your consulting practice on Books).
 *
 * THE DEFAULT IS BOOKS (`expert`). Founder, 29 Aug 2026: "keep the default view
 * as books for all."
 *
 * It was Flow, on the reasoning that the gentlest surface is the kindest door.
 * That is the wrong default for who actually opens Karr: a practice, and the
 * businesses a practice keeps books for. Flow hides the journals, the ledgers
 * and most of the toolkit, so the person who came to do accounting had to find
 * a switch before the software would show them the accounts — and a default
 * that has to be corrected on the first day is not a default, it is a detour.
 *
 * A stored choice always wins. Only somebody who has actually pressed the
 * switch has one, so this changes what people LAND in, and overrides nobody
 * who has said what they want.
 *
 * flow-10 (FLOW_SCREEN_SPEC.md §11.2, decision A, founder 24 Sep 2026): a NEW
 * business created by its own owner, with no practice behind it, opens in
 * Flow (`defaultLevelForNewOrg`); the choice is stored at creation, so it is
 * a stored choice like any other and the switch still changes it. A practice
 * and the businesses a practice keeps books for stay in Books, as does every
 * org that existed before (nothing stored: Books).
 *
 * @since 2.0.0
 */
import { create } from 'zustand';
import type { ExperienceLevel } from '../config/experienceLevel';

/**
 * flow-10 (decision A): the view a newly created org opens in. Flow for a
 * business its own owner set up with no practice link; Books for a practice
 * office and for a business a practice set up or keeps books for.
 */
export function defaultLevelForNewOrg(o: { isPractice: boolean; practiceLinked: boolean }): ExperienceLevel {
    return o.isPractice || o.practiceLinked ? 'expert' : 'business_owner';
}

// ─────────────────────────────────────────────
// Storage helpers
// ─────────────────────────────────────────────

function getStorageKey(orgId: string): string {
    return `karr_experience_level_${orgId}`;
}

function loadLevel(orgId: string): ExperienceLevel {
    if (!orgId) return 'expert';
    try {
        const stored = localStorage.getItem(getStorageKey(orgId));
        // ga-7 two-view collapse: the retired 'finance_manager' maps to Standard.
        if (stored === 'finance_manager') return 'expert';
        if (stored === 'business_owner' || stored === 'expert') {
            return stored;
        }
    } catch {
        // localStorage unavailable (SSR, privacy mode)
    }
    // Books. The full accounting view is the one Karr is for; Flow is one
    // press of the header switch away, and the choice is then remembered.
    return 'expert';
}

function saveLevel(orgId: string, level: ExperienceLevel): void {
    if (!orgId) return;
    try {
        localStorage.setItem(getStorageKey(orgId), level);
    } catch {
        // silently fail
    }
}

// ─────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────

interface ExperienceLevelState {
    /** Current experience level */
    level: ExperienceLevel;
    /** The org this level is associated with */
    orgId: string;
    /** Initialize from localStorage for a given org */
    initialize: (orgId: string) => void;
    /** Switch to a different level */
    setLevel: (level: ExperienceLevel) => void;
}

export const useExperienceLevel = create<ExperienceLevelState>((set, get) => ({
    // Matches loadLevel's default, so the very first paint — before any org is
    // known — is already Books and nothing flips under the reader.
    level: 'expert',
    orgId: '',

    initialize: (orgId: string) => {
        const level = loadLevel(orgId);
        set({ level, orgId });
    },

    setLevel: (level: ExperienceLevel) => {
        const { orgId } = get();
        saveLevel(orgId, level);
        set({ level });
    },
}));
